import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useTheme } from '../context/ThemeContext'
import { useTenantContext, saveTenantSelection } from '../hooks/useTenantContext'
import { useViolationSummary } from '../hooks/useComplianceData'
import { orgKeys } from '../hooks/useOrganizations'
import {
  consumeSetupAutoRoute,
  needsSelfServeSetup,
  SETUP_ROUTE,
} from '../lib/onboarding-setup'
import { AppHeader } from './carbon/AppHeader'
import { inertWhen } from './carbon/a11y'
import { AppShell } from './carbon/AppShell'
import { Breadcrumb } from './carbon/Breadcrumb'
import { HeaderButton } from './carbon/HeaderButton'
import { IconChat, IconMoon, IconSignOut, IconSun } from './carbon/icons'
import { SideNav } from './carbon/SideNav'
import { initialsOf } from './carbon/initials'
import { TenantSwitcher } from './carbon/TenantSwitcher'
import type { SideNavGroup, SideNavUserAction } from './carbon/types'
import { deriveBreadcrumbs, deriveNavCounts, filterNavGroups, NAVIGATION_GROUPS } from './layout-nav'
import { formatAssessedClock } from './common/LastAssessed'
import { lastAssessmentAt } from '@/lib/tenant-assessed'
import NotificationsBell from './Notifications/NotificationsBell'
import SetupBanner from './Onboarding/SetupBanner'

interface CurrentUserInfo {
  isGlobalAdmin: boolean
  isPlatformAdmin?: boolean
  email?: string
  displayName?: string
  role?: string
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function humanizeRole(role: string | null | undefined): string {
  if (!role) return ''
  return role.replace(/([a-z])([A-Z])/g, '$1 $2')
}

// ----------------------------------------------------------------------------
// Layout
// ----------------------------------------------------------------------------

interface Props {
  children: React.ReactNode
}

export default function Layout({ children }: Props) {
  const [navOpen, setNavOpen] = useState(false)
  const [currentUserInfo, setCurrentUserInfo] = useState<CurrentUserInfo | null>(null)
  const [isLoadingUser, setIsLoadingUser] = useState(true)
  const location = useLocation()
  const mainRef = useRef<HTMLElement>(null)
  const { accounts, instance } = useMsal()
  const { theme, toggleTheme } = useTheme()
  const { defaultTenantId, userTenants, isGlobalAdmin: tenantCtxGlobalAdmin, isLoading: tenantsLoading } = useTenantContext()

  // Reset scroll on route changes. The scroll container is <main> (not window),
  // so browser scroll restoration doesn't apply. Keyed on pathname only —
  // ?tab=... changes within Security Center / Baseline Compliance keep position.
  // The phone nav drawer closes on any navigation.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 })
    setNavOpen(false)
  }, [location.pathname])

  const currentUser = accounts[0] || null

  // Fetch current user info from API. Secure-by-default if the call fails
  // (treat user as non-admin).
  useEffect(() => {
    const abortController = new AbortController()

    const fetchCurrentUser = async () => {
      try {
        if (currentUser) {
          const userInfo = await api.getCurrentUser()
          if (!abortController.signal.aborted) {
            setCurrentUserInfo(userInfo)
          }
        }
      } catch (error) {
        if (abortController.signal.aborted) return
        console.error('Failed to fetch current user info:', error)
        setCurrentUserInfo({ isGlobalAdmin: false })
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingUser(false)
        }
      }
    }

    fetchCurrentUser()

    return () => {
      abortController.abort()
    }
  }, [currentUser])

  const isGlobalAdmin = currentUserInfo?.isGlobalAdmin || false
  const isPlatformAdmin = currentUserInfo?.isPlatformAdmin || false

  // Pending-organization count drives the Admin Portal nav count. Scoped to
  // admins so non-admin users don't hit a 403 from the Platform-Admin-only
  // endpoint. Key matches `useOrganizations({ pageSize: 200 })` on the
  // management page, so navigating into it doesn't refetch.
  const orgPendingQuery = useQuery({
    queryKey: orgKeys.list({ pageSize: 200 }),
    queryFn: () => api.getOrganizations({ pageSize: 200 }),
    enabled: isGlobalAdmin,
    staleTime: 60 * 1000,
    retry: 1,
  })
  const orgPendingCount = useMemo(
    () => (orgPendingQuery.data?.items ?? []).filter(o => o.status === 'PendingApproval').length,
    [orgPendingQuery.data],
  )

  // Side-nav counts (D28) for the selected tenant, from the same query keys
  // the pages use, so the cache is shared. No tenant (a global admin on "All
  // tenants") means no counts; every count is hidden while it loads.
  const countTenant = defaultTenantId
  const statsQuery = useQuery({
    queryKey: ['dashboard', countTenant],
    queryFn: () => (countTenant ? api.getDashboardStats(countTenant) : Promise.resolve(null)),
    enabled: !!countTenant,
    retry: 1,
  })
  const summaryQuery = useViolationSummary(countTenant, { includeUnstarredFrameworks: false })
  const insightsQuery = useQuery({
    queryKey: ['user-insights', countTenant],
    queryFn: () => api.getUserInsights(countTenant!),
    enabled: !!countTenant,
    retry: 1,
  })

  const counts = useMemo(
    () =>
      deriveNavCounts({
        stats: statsQuery.data,
        statsLoading: statsQuery.isLoading,
        summary: summaryQuery.data,
        summaryLoading: summaryQuery.isLoading,
        insights: insightsQuery.data,
        insightsLoading: insightsQuery.isLoading,
        pendingOrgs: orgPendingCount,
      }),
    [statsQuery.data, statsQuery.isLoading, summaryQuery.data, summaryQuery.isLoading, insightsQuery.data, insightsQuery.isLoading, orgPendingCount],
  )

  // Admin-only and platform-admin-only items stay hidden while the user loads (layout-nav.tsx).
  const visibleGroups: SideNavGroup[] = useMemo(
    () =>
      filterNavGroups(NAVIGATION_GROUPS, { isLoadingUser, isGlobalAdmin, isPlatformAdmin }).map((group) => ({
        label: group.label,
        items: group.items.map((item) => ({
          to: item.href,
          label: item.name,
          lead: item.lead,
          count: item.countKey ? counts[item.countKey] : undefined,
        })),
      })),
    [isLoadingUser, isGlobalAdmin, isPlatformAdmin, counts],
  )

  // Pre-warm the tenants query so the switcher renders immediately without a
  // fetch waterfall when the user changes routes. Doubles as the data source
  // for the first-run setup gate below.
  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: api.getTenants,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  // First-run setup gate (wizard blueprint D2 — soft gating). An eligible
  // self-service customer is auto-routed into /setup once per SPA session;
  // if they come back ("skip for now"), the banner keeps the unfinished
  // state visible without trapping them. Eligibility is the pure
  // needsSelfServeSetup predicate; platform admins and fleet tenants never
  // qualify.
  const needsSetup =
    !isLoadingUser && needsSelfServeSetup(currentUserInfo, tenantsQuery.data)
  const navigate = useNavigate()
  useEffect(() => {
    if (needsSetup && consumeSetupAutoRoute()) {
      navigate(SETUP_ROUTE)
    }
  }, [needsSetup, navigate])

  // Tenant switching. Phase 12 decision: pages initialize their tenant from
  // useTenantContext on mount, so a full reload is the cleanest way to
  // propagate the change everywhere with zero risk of stale data.
  const handleTenantSelect = (tenantId: string | undefined) => {
    saveTenantSelection(tenantId)
    setNavOpen(false)
    window.location.reload()
  }
  const showSwitcher = !tenantsLoading && (tenantCtxGlobalAdmin || userTenants.length > 1)
  const tenantOptions = useMemo(
    () => userTenants.map(t => ({ id: t.tenantId, name: t.name, domain: t.domain || undefined })),
    [userTenants],
  )

  const activeTenant = defaultTenantId ? userTenants.find(t => t.tenantId === defaultTenantId) : undefined
  // "at 06:12 today", "at 22:05 yesterday", "on Oct 2 at 07:00": the same phrase as the pages' provenance line.
  // Only the selected tenant's real assessment: no line for All tenants, a never-scanned tenant (its creation
  // stamp or the MinValue sentinel) or an unparseable value.
  const assessedAt = lastAssessmentAt(activeTenant)
  const lastAssessed = assessedAt ? formatAssessedClock(assessedAt) : null

  const userName = currentUser?.name || currentUser?.username || 'Guardian User'
  const userActions: SideNavUserAction[] = [
    { id: 'feedback', label: 'Send feedback', icon: <IconChat />, href: 'https://github.com/iuvonauts/guardian-feedback/issues' },
    {
      id: 'sign-out',
      label: 'Sign out',
      icon: <IconSignOut />,
      onClick: () => {
        instance.logoutPopup({
          postLogoutRedirectUri: window.location.origin,
          mainWindowRedirectUri: window.location.origin,
        })
      },
    },
  ]

  const closeNav = useCallback(() => setNavOpen(false), [])

  return (
    <AppShell
      navOpen={navOpen}
      onCloseNav={closeNav}
      header={
        <AppHeader
          onMenu={() => setNavOpen(open => !open)}
          menuOpen={navOpen}
          navId="side-nav"
          meta={
            lastAssessed ? (
              <span className="tabular-nums">Last assessed {lastAssessed}</span>
            ) : undefined
          }
        >
          <NotificationsBell />
          <HeaderButton
            label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            icon={theme === 'dark' ? <IconSun /> : <IconMoon />}
            onClick={toggleTheme}
          />
        </AppHeader>
      }
      nav={
        <SideNav
          id="side-nav"
          groups={visibleGroups}
          open={navOpen}
          onClose={closeNav}
          tenantSlot={
            <TenantSwitcher
              tenants={tenantOptions}
              currentId={defaultTenantId}
              onSelect={handleTenantSelect}
              isLoading={tenantsLoading}
              // "All tenants" only where it aggregates something: a global admin with more than one tenant.
              allTenantsLabel={tenantCtxGlobalAdmin && userTenants.length > 1 ? 'All tenants' : undefined}
              switchable={showSwitcher}
            />
          }
          user={currentUser ? { name: userName, role: humanizeRole(currentUserInfo?.role), initials: initialsOf(userName) } : null}
          userActions={userActions}
        />
      }
    >
      {/* Behind the open phone drawer, <main> is inert: out of the tab order and the accessibility tree. */}
      <main ref={mainRef} id="main" tabIndex={-1} {...inertWhen(navOpen)} className="min-w-0 flex-1 overflow-auto focus:outline-none">
        <div className="mx-auto max-w-[1584px] px-8 pb-16 pt-6 max-c-md:px-4 max-c-md:pb-12 max-c-md:pt-4">
          <Breadcrumb items={deriveBreadcrumbs(location.pathname)} />
          {/* Unfinished-setup reminder (soft gate, blueprint D2). */}
          {needsSetup && (
            <div className="mt-4">
              <SetupBanner />
            </div>
          )}
          {children}
        </div>
      </main>
    </AppShell>
  )
}
